<template>
  <!--
    `attach="#app"` é obrigatório, não estético: o Tailwind deste projeto é gerado com
    `important: '#app'` (tailwind.config.js), então toda utility sai como `#app .flex { … }`.
    O destino padrão do v-dialog é `body > .v-overlay-container`, irmão de `#app` — lá
    nenhuma utility casa (nem as do CoverageModeSelector/UnidentifiedRow das miniaturas) e o
    aviso aparecia sem superfície, por cima do modal "Criar plantão". Mesmo padrão de
    QualificationTab.vue. A superfície (fundo, raio, sombra) fica em CSS scoped para não
    depender disso; o z-index do overlay do Vuetify (2000+) fica acima do Modal host (z-50).
  -->
  <v-dialog
    :model-value="modelValue"
    :max-width="isDesktop ? 720 : undefined"
    :fullscreen="!isDesktop"
    scrollable
    attach="#app"
    scrim="rgba(22, 22, 29, 0.55)"
    :aria-labelledby="titleId"
    data-testid="placeholder-blocked-dialog"
    @update:model-value="onDialogUpdate"
  >
    <div
      class="placeholder-blocked"
      :class="isDesktop ? 'placeholder-blocked--desktop' : 'placeholder-blocked--mobile'"
    >
      <header class="placeholder-blocked__header">
        <div class="flex items-start gap-4">
          <span class="placeholder-blocked__alert-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path :d="mdiAlertOutline" fill="currentColor" />
            </svg>
          </span>
          <div class="flex flex-col gap-2 min-w-0">
            <h2
              :id="titleId"
              ref="titleRef"
              tabindex="-1"
              class="placeholder-blocked__title"
              data-testid="placeholder-blocked-title"
            >
              Este médico não pode ser usado como coringa
            </h2>
            <div class="flex flex-wrap items-center gap-2">
              <span
                v-if="doctorName"
                class="placeholder-blocked__doctor-chip"
                data-testid="placeholder-blocked-subtitle"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path :d="mdiAccountOutline" fill="currentColor" />
                </svg>
                <span class="truncate">{{ doctorName }}</span>
              </span>
              <span class="placeholder-blocked__new-badge" data-testid="placeholder-blocked-new-badge">
                Novo
              </span>
            </div>
          </div>
        </div>

      </header>

      <div class="placeholder-blocked__scroll">
        <div class="placeholder-blocked__intro">
          <p class="placeholder-blocked__explanation" data-testid="placeholder-blocked-explanation">
            Até agora, quando ainda não sabíamos quem faria o plantão, usávamos a Dra. Ana Lavradas no
            lugar. Isso misturava vagas abertas com plantões cobertos e bagunçava relatórios e
            pagamentos. Agora existe a opção <strong>{{ UNIDENTIFIED_COVERAGE_LABEL }}</strong>: o
            plantão fica marcado como coberto, sem precisar indicar um médico.
          </p>
        </div>

      <div class="placeholder-blocked__body">
        <p class="placeholder-blocked__section-title">Como registrar agora</p>

        <ol class="placeholder-blocked__steps" data-testid="placeholder-blocked-steps">
          <li v-for="step in steps" :key="step.number" class="placeholder-blocked__step">
            <span class="placeholder-blocked__number" aria-hidden="true">{{ step.number }}</span>
            <div class="flex flex-col gap-3 min-w-0">
              <span
                class="placeholder-blocked__step-text"
                :data-testid="`placeholder-blocked-step-${step.number}-text`"
              >
                {{ step.text }}
              </span>

              <div
                role="img"
                :aria-label="step.illustrationLabel"
                class="placeholder-blocked__illustration"
                :data-testid="`placeholder-blocked-illustration-${step.number}`"
              >
                <!-- Miniatura somente-leitura: inerte (sem foco/clique) e fora da árvore de acessibilidade -->
                <div inert aria-hidden="true" class="pointer-events-none select-none">
                  <CoverageModeSelector
                    v-if="step.number === 1"
                    :model-value="CoverageMode.UNIDENTIFIED"
                    :allow-unidentified="true"
                    :label="COVERAGE_BLOCK_TITLE"
                    disabled
                    class="placeholder-blocked__selector"
                  />

                  <!-- Marcação estática com o visual do CoverageResponsibleField (sem buscar a lista) -->
                  <div v-else-if="step.number === 2" class="flex flex-col gap-1">
                    <div class="placeholder-blocked__field placeholder-blocked__field--focused">
                      <span class="placeholder-blocked__field-label">{{ COVERAGE_RESPONSIBLE_LABEL }}</span>
                      <span class="flex items-center justify-between gap-2">
                        <span class="truncate">{{ EXAMPLE_RESPONSIBLE }}</span>
                        <svg viewBox="0 0 24 24" width="18" height="18" class="shrink-0 text-[#68688D]">
                          <path :d="mdiMenuUp" fill="currentColor" />
                        </svg>
                      </span>
                    </div>
                    <ul class="placeholder-blocked__menu">
                      <li
                        v-for="option in EXAMPLE_RESPONSIBLE_OPTIONS"
                        :key="option"
                        :class="{ 'placeholder-blocked__menu-item--active': option === EXAMPLE_RESPONSIBLE }"
                      >
                        {{ option }}
                      </li>
                    </ul>
                  </div>

                  <div v-else-if="step.number === 3" class="placeholder-blocked__field">
                    <span class="placeholder-blocked__field-label">{{ COVERAGE_NOTE_LABEL }}</span>
                    <span class="block min-h-[2.5rem] break-words">{{ EXAMPLE_NOTE }}</span>
                  </div>

                  <div v-else class="placeholder-blocked__card">
                    <UnidentifiedRow :responsible-name="EXAMPLE_RESPONSIBLE" />
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ol>

        <div class="placeholder-blocked__help" data-testid="placeholder-blocked-help">
          <svg viewBox="0 0 24 24" width="20" height="20" class="placeholder-blocked__help-icon" aria-hidden="true">
            <path :d="mdiInformationOutline" fill="currentColor" />
          </svg>
          <div class="flex flex-col gap-2 min-w-0">
            <p data-testid="placeholder-blocked-help-flag">
              Não aparece a opção '{{ UNIDENTIFIED_COVERAGE_LABEL }}'? Peça para quem administra a escala
              ligar 'Permite plantão ocupado sem identificação' na configuração da escala.
            </p>
            <p data-testid="placeholder-blocked-help-management">
              Se a Dra. Ana vai mesmo fazer este plantão, fale com a gestão de escalas.
            </p>
          </div>
        </div>
      </div>
      </div>

      <footer class="placeholder-blocked__footer">
        <OutlineButton
          height="44px"
          :width="isDesktop ? '140px' : '100%'"
          color="grey"
          class="placeholder-blocked__button"
          data-testid="placeholder-blocked-dismiss"
          @click="dismiss"
        >
          Entendi
        </OutlineButton>
        <FilledButton
          v-if="allowUnidentified"
          height="44px"
          :width="isDesktop ? 'auto' : '100%'"
          color="primary"
          class="placeholder-blocked__button placeholder-blocked__button--primary"
          data-testid="placeholder-blocked-use-unidentified"
          @click="useUnidentified"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" class="mr-2 shrink-0" aria-hidden="true">
            <path :d="mdiCheckCircleOutline" fill="currentColor" />
          </svg>
          Usar {{ UNIDENTIFIED_COVERAGE_LABEL }}
        </FilledButton>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  mdiAccountOutline,
  mdiAlertOutline,
  mdiCheckCircleOutline,
  mdiInformationOutline,
  mdiMenuUp
} from '@mdi/js'
import CoverageModeSelector from '@/components/Coverage/CoverageModeSelector.vue'
import UnidentifiedRow from '@/components/designSystem/Table/StandardRow/unidentified.vue'
import { UNIDENTIFIED_COVERAGE_COLORS as COLORS } from '@/components/designSystem/Table/StandardRow/unidentifiedCoverageStyles'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import {
  COVERAGE_NOTE_LABEL,
  COVERAGE_RESPONSIBLE_LABEL,
  UNIDENTIFIED_COVERAGE_LABEL
} from '@/utils/coverage'

/**
 * Aviso descritivo do `PLACEHOLDER_DOCTOR_BLOCKED`: explica por que o médico "coringa"
 * foi recusado e ensina, com miniaturas dos próprios componentes, a usar
 * "Ocupado sem identificação". Estado via `usePlaceholderDoctorBlocked`.
 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    doctorName?: string | null
    /** A escala permite "Ocupado sem identificação": mostra o botão primário. */
    allowUnidentified: boolean
  }>(),
  { doctorName: null }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  useUnidentified: []
  close: []
}>()

const COVERAGE_BLOCK_TITLE = 'Cobertura do plantão'
const EXAMPLE_RESPONSIBLE = 'Equipe CLT da Unidade'
// Responsáveis reais do seed do backend, na mesma ordem
const EXAMPLE_RESPONSIBLE_OPTIONS = [
  EXAMPLE_RESPONSIBLE,
  'Servidor público da Unidade',
  'Outra empresa / cooperativa',
  'Parceiro indicará (a identificar)'
]
const EXAMPLE_NOTE = 'Escala enviada pela unidade até dia 25'

const steps = [
  {
    number: 1,
    text: `Em "${COVERAGE_BLOCK_TITLE}", escolha "${UNIDENTIFIED_COVERAGE_LABEL}"`,
    illustrationLabel: `Seletor "${COVERAGE_BLOCK_TITLE}" com a opção "${UNIDENTIFIED_COVERAGE_LABEL}" marcada`
  },
  {
    number: 2,
    text: `Em "${COVERAGE_RESPONSIBLE_LABEL}", escolha quem cobre, ex. "${EXAMPLE_RESPONSIBLE}"`,
    illustrationLabel: `Campo "${COVERAGE_RESPONSIBLE_LABEL}" com a lista aberta e "${EXAMPLE_RESPONSIBLE}" escolhido`
  },
  {
    number: 3,
    text: `Se quiser, escreva uma observação (ex.: "${EXAMPLE_NOTE}")`,
    illustrationLabel: `Campo de observação preenchido com "${EXAMPLE_NOTE}"`
  },
  {
    number: 4,
    text: 'Salve: o plantão aparece na grade com o card cinza listrado "Ocupado · s/ identificação"',
    illustrationLabel: `Card cinza listrado "Ocupado · s/ identificação" com a etiqueta "${EXAMPLE_RESPONSIBLE}"`
  }
] as const

// id único por instância (Vue 3.3 não tem `useId`; um 2º <script> quebra o ts checker do build)
const titleId = `placeholder-blocked-title-${Math.random().toString(36).slice(2, 10)}`
const titleRef = ref<HTMLElement | null>(null)
const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

// Foco inicial no título. O VDialog (3.3) foca o próprio conteúdo num `nextTick` ao abrir
// e não emite `afterEnter`; o `setTimeout` (macrotask) roda depois disso.
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    setTimeout(() => titleRef.value?.focus({ preventScroll: true }), 0)
  },
  { immediate: true }
)

function closeDialog() {
  emit('update:modelValue', false)
  emit('close')
}

// ESC e clique fora chegam como `update:modelValue(false)` do v-dialog.
function onDialogUpdate(value: boolean) {
  if (!value) closeDialog()
}

function dismiss() {
  closeDialog()
}

function useUnidentified() {
  emit('useUnidentified')
  closeDialog()
}

const text = COLORS.text
</script>

<style scoped>
/* Tokens do produto (tailwind.config.js): primary #2F7F91, primary5 #D8EEF3, primary10 #84C8D7,
   primary40 #235E6C, alert #F18F1B, textPrimary #16161D, lightGray30 #D8D8E2. */
.placeholder-blocked {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  background: #fff;
  color: #16161d;
  text-align: left;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.placeholder-blocked--desktop {
  max-height: 85vh;
  border-radius: 16px;
  box-shadow: 0 24px 48px -12px rgba(22, 22, 29, 0.28), 0 4px 12px rgba(22, 22, 29, 0.08);
}
.placeholder-blocked--mobile {
  height: 100%;
}

/* Cabeçalho */
.placeholder-blocked__header {
  flex-shrink: 0;
  padding: 24px 32px 20px;
  border-bottom: 1px solid #e8e8ef;
}
.placeholder-blocked__alert-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fdf0e0;
  color: #d97a0c;
}
.placeholder-blocked__title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #16161d;
  outline: none;
}
.placeholder-blocked__doctor-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 3px 10px;
  border-radius: 9999px;
  border: 1px solid #d8d8e2;
  background: #f7f7fa;
  font-size: 13px;
  font-weight: 500;
  color: #3e3e5a;
}
.placeholder-blocked__doctor-chip svg {
  flex-shrink: 0;
  color: #68688d;
}
.placeholder-blocked__new-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 9999px;
  background: #d8eef3;
  color: #235e6c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.placeholder-blocked__explanation {
  max-width: 62ch;
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #3e3e5a;
}
.placeholder-blocked__explanation strong {
  color: #16161d;
  font-weight: 700;
}

/* Área rolável: explicação (branca) + passos sobre painel tingido */
.placeholder-blocked__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.placeholder-blocked__intro {
  padding: 20px 32px 24px;
  border-bottom: 1px solid #e3eef2;
}
.placeholder-blocked__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 32px 28px;
  background: #f3f9fb;
}
.placeholder-blocked__section-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #235e6c;
}

/* Stepper vertical */
.placeholder-blocked__steps {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}
.placeholder-blocked__step {
  position: relative;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  column-gap: 16px;
  padding-bottom: 24px;
}
.placeholder-blocked__step:last-child {
  padding-bottom: 0;
}
/* Linha pontilhada ligando os círculos; o último passo não tem linha abaixo */
.placeholder-blocked__step:not(:last-child)::before {
  content: '';
  position: absolute;
  top: 34px;
  bottom: 6px;
  left: 13px;
  border-left: 2px dashed #84c8d7;
}
.placeholder-blocked__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  background: #2f7f91;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 0 0 4px #f3f9fb;
}
.placeholder-blocked__step-text {
  padding-top: 4px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #16161d;
}
.placeholder-blocked__illustration {
  max-width: 460px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e3e3ec;
  background: #fff;
  box-shadow: 0 1px 2px rgba(22, 22, 29, 0.05);
}
/* A dica longa do seletor não cabe na miniatura */
.placeholder-blocked__selector :deep([data-testid='coverage-mode-unidentified-hint']) {
  display: none;
}
/* Miniatura: o `disabled` do seletor apagaria o segmento escolhido; aqui ele é só ilustração */
.placeholder-blocked__selector :deep(button:disabled) {
  opacity: 1 !important;
  cursor: default !important;
}
.placeholder-blocked__field {
  position: relative;
  padding: 14px 12px 8px;
  border: 1px solid #d8d8e2;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  color: v-bind(text);
}
.placeholder-blocked__field--focused {
  border: 2px solid #2f7f91;
}
.placeholder-blocked__field-label {
  position: absolute;
  top: -8px;
  left: 8px;
  padding: 0 4px;
  background: #fff;
  font-size: 11px;
  color: #68688d;
}
.placeholder-blocked__field--focused .placeholder-blocked__field-label {
  color: #2f7f91;
}
.placeholder-blocked__menu {
  margin: 0;
  padding: 4px 0;
  list-style: none;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(22, 22, 29, 0.12);
  font-size: 13px;
  color: v-bind(text);
}
.placeholder-blocked__menu li {
  padding: 7px 12px;
}
.placeholder-blocked__menu-item--active {
  background: #d8eef3;
  font-weight: 600;
}
.placeholder-blocked__card {
  width: 170px;
}

/* Callout de ajuda */
.placeholder-blocked__help {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #d8d8e2;
  background: #fff;
  font-size: 13px;
  line-height: 20px;
  color: #3e3e5a;
}
.placeholder-blocked__help p {
  margin: 0;
}
.placeholder-blocked__help-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: #2f7f91;
}

/* Rodapé fixo (fora da área rolável) */
.placeholder-blocked__footer {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding: 16px 32px;
  border-top: 1px solid #e8e8ef;
  background: #fff;
}
.placeholder-blocked__button {
  flex: 0 0 auto;
  letter-spacing: normal;
}
.placeholder-blocked__button--primary {
  padding-left: 20px !important;
  padding-right: 20px !important;
}

@media (max-width: 767px) {
  .placeholder-blocked__header {
    padding: 20px 16px 16px;
  }
  .placeholder-blocked__intro {
    padding: 16px 16px 20px;
  }
  .placeholder-blocked__title {
    font-size: 18px;
    line-height: 26px;
  }
  .placeholder-blocked__body {
    padding: 20px 16px 24px;
  }
  .placeholder-blocked__illustration {
    max-width: 100%;
  }
  .placeholder-blocked__selector :deep(button) {
    padding: 8px 6px !important;
    font-size: 13px !important;
    line-height: 18px;
  }
  /* Mobile: coluna, largura total, primário em cima */
  .placeholder-blocked__footer {
    flex-direction: column-reverse;
    align-items: stretch;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }
  .placeholder-blocked__button {
    width: 100%;
  }
}
</style>
